import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Shield, TrendingUp, ArrowRight, Layers } from 'lucide-react';

const Partnership = () => {

    const benefits = [
        {
            icon: <Globe className="w-5 h-5 text-primary" />,
            title: "Global Market Access",
            desc: "Trade Forex, US Stocks, Commodities, and Crypto seamlessly."
        },
        {
            icon: <Layers className="w-5 h-5 text-primary" />,
            title: "Advanced Infrastructure",
            desc: "State-of-the-art platforms and deep liquidity provided by Equity."
        },
        {
            icon: <Shield className="w-5 h-5 text-primary" />,
            title: "Institutional Security",
            desc: "Benefit from institutional-grade execution and risk management tools."
        },
        {
            icon: <TrendingUp className="w-5 h-5 text-primary" />,
            title: "Exclusive Insights",
            desc: "Market programs powered by Time Technique Trading™ & Equity expertise."
        }
    ];

    return (
        /*
           FIX 1: Adjusted Padding
           - lg:px-10 (was px-20): Reduces horizontal padding on iPad Pro (1024px) to give content more room.
           - xl:px-20: Restores the wide padding for large desktops.
        */
        <section className="relative w-full py-14 px-4 sm:px-10 lg:px-10 xl:px-20 font-poppins overflow-hidden">

            <div className="max-w-7xl mx-auto relative z-10">

                {/* --- Logo Union Header --- */}
                <div className="flex flex-col items-center justify-center mb-10">
                    {/* Logo section is currently commented out in your code, keeping as is. */}
                </div>

                {/* --- Main Content Grid --- */}
                {/*
                   FIX 2: Adjusted Grid Gap
                   - lg:gap-10 (was gap-20): Reduces the empty space between columns on tablets.
                   - xl:gap-20: Restores large gap on desktops.
                */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-10 xl:gap-20 items-center">

                    {/* Left Column: The Narrative */}
                    <div className="space-y-8">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                            <span className="text-xs font-bold tracking-widest text-primary uppercase">
                            Strategic Partnership
                        </span>
                        </motion.div>
                        <motion.h2
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            /* FIX 3: Typography Scaling */
                            className="text-3xl sm:text-4xl md:text-5xl font-bold text-dark dark:text-light"
                        >
                            <span className="block leading-[1.2]">Empowering Traders in</span>
                            <span className="block leading-[1.2] text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                                Dubai and Beyond
                            </span>
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-gray-600 dark:text-gray-400 text-base sm:text-lg leading-relaxed"
                        >
                            We are proud to announce our exclusive partnership with <span className="font-bold text-dark dark:text-light">Equity</span>, one of the largest and most respected brokerage firms in the Middle East.
                        </motion.p>

                        {/* "Why This Matters" Box */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="bg-primary/5 border border-primary/10 rounded-2xl p-6 text-left"
                        >
                            <h4 className="text-primary font-bold text-lg mb-2">Why This Partnership Matters</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                By combining Fortune Signals’ rare, predictive trading methodologies with Equity’s robust trading ecosystem, we give traders a complete edge:
                            </p>
                            <ul className="mt-4 space-y-2">
                                <li className="flex items-start sm:items-center gap-2 text-sm font-medium text-dark dark:text-light">
                                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 sm:mt-0"></div>
                                    <span>Education + Mentorship + Professional Access</span>
                                </li>
                                <li className="flex items-start sm:items-center gap-2 text-sm font-medium text-dark dark:text-light">
                                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 sm:mt-0"></div>
                                    <span>Time-Based Forecasting integrated with Live Execution</span>
                                </li>
                                <li className="flex items-start sm:items-center gap-2 text-sm font-medium text-dark dark:text-light">
                                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 sm:mt-0"></div>
                                    <span>Confidence of trading through a Regulated Broker</span>
                                </li>
                            </ul>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="pt-2"
                        >
                            <button className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-primary to-secondary hover:from-[#005085] hover:to-[#085a8c] text-white font-bold rounded-full shadow-lg shadow-primary/25 transition-all flex items-center justify-center gap-2 group text-sm sm:text-base">
                                Start Trading with Equity
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </motion.div>
                    </div>

                    {/* Right Column: Feature Cards */}
                    {/*
                       Note: Keeping sm:grid-cols-2 is fine now because we increased the column width
                       by reducing the parent padding/gap.
                    */}
                    <div className="grid gap-4 sm:grid-cols-2">
                        {benefits.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                                className="bg-white/50 dark:bg-bg-dark-2/50 backdrop-blur-sm border border-gray-100 dark:border-white/5 p-5 lg:p-6 rounded-2xl hover:border-primary/30 transition-all hover:shadow-lg group"
                            >
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-light group-hover:text-white transition-colors duration-300 shrink-0">
                                    {item.icon}
                                </div>
                                <h3 className="font-bold text-dark dark:text-light mb-2">{item.title}</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                    {item.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>

                </div>
            </div>
        </section>
    );
};

export default Partnership;